import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { EXTERNAL_APPS } from "../../constants";
import map from "../../images/map.svg";
import inspire from "../../images/inspire2.svg";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(28, 71, 59, 0.7); /* theme.green with 70% opacity */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100005; /* Higher than other modals but not as high as menus */
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.5s ease, visibility 0.5s ease;
  padding: 40px;

  /* Prevent body scroll when modal is open */
  &.modal-open {
    overflow: hidden;
  }
`;

const ModalContainer = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 1000px;
  height: 600px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: ${(props) => (props.visible ? "scale(1)" : "scale(0.8)")};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: transform 0.5s ease, opacity 0.5s ease;
  padding: 10px;

  @media (max-width: 768px) {
    max-width: 95%;
    height: 500px;
    padding: 10px;
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1000px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -15px;
  right: -18px;
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  z-index: 15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #666;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    color: ${({ theme }) => theme.green};
    border-color: ${({ theme }) => theme.green};
    transform: scale(1.1);
  }

  &:before {
    content: "×";
    font-weight: 400;
    line-height: 1;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  height: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const CardWrapper = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 24px;
  left: 80px;
  right: 80px;
  background: #ffffff;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 768px) {
    bottom: 20px;
    left: 40px;
    right: 40px;
    padding: 20px 14px;
    gap: 6px;
  }
`;

const CardTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.ivy};
  font-size: 22px;
  font-weight: 400;
  color: ${({ theme }) => theme.green};
  margin: 0;
  line-height: 1.1;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CardDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.green};
  opacity: 0.7;
  margin: 0;
  line-height: 1.3;
  flex: 1;
  display: flex;
  align-items: center;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const CardButton = styled.button`
  background: #307361;
  color: white;
  border: none;
  padding: 10px 20px;
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  max-width: 80%;
  width: 100%;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
  }
  transition: opacity 0.3s ease;
  outline: none;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
  }
`;

const ModalPopup = () => {
  const [visible, setVisible] = useState(false);
  const [show, setShow] = useState(false);
  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setShow(false);
      sessionStorage.setItem("modalPopupShown", "true");
    }, 500);
  }, []);

  useEffect(() => {
    // Check if modal was already shown in this session
    const modalShown = sessionStorage.getItem("modalPopupShown");

    if (!modalShown) {
      const timer = setTimeout(() => {
        setShow(true);
        setTimeout(() => setVisible(true), 50);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, []);

  // Prevent body scroll when modal is visible
  useEffect(() => {
    if (visible) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [visible]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && visible) {
        handleClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [visible, handleClose]);

  const handlePlanTrip = () => {
    // handleClose();
    // Open interactive map in a new tab
    try {
      window.open(
        EXTERNAL_APPS.interactiveMap,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (err) {
      // Fallback: create anchor to ensure rel attributes
      const a = document.createElement("a");
      a.href = EXTERNAL_APPS.interactiveMap;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const handleGetInspired = () => {
    // handleClose();
    // Open inspiring mode in a new tab
    try {
      window.open(EXTERNAL_APPS.inspiringMode, "_blank", "noopener,noreferrer");
    } catch (err) {
      const a = document.createElement("a");
      a.href = EXTERNAL_APPS.inspiringMode;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay visible={visible} onClick={handleOverlayClick}>
      <ModalWrapper>
        <CloseButton onClick={handleClose} />
        <ModalContainer visible={visible}>
          <CardsContainer>
            {/* Interactive Map Card */}
            <CardWrapper>
              <CardImage src={map} alt="Explore Fès-Meknès" />
              <CardContent>
                <CardTitle>
                  {t("exploreTitle", "Explore Fès-Meknès at Your Own Pace")}
                </CardTitle>
                <CardDescription>
                  {t(
                    "exploreDescription",
                    "Uncover hidden gems, plan your itinerary, and let our interactive map guide you through culture, nature, and history."
                  )}
                </CardDescription>
                <CardButton onClick={handlePlanTrip}>
                  {t("startExploring", "Start Exploring")}
                </CardButton>
              </CardContent>
            </CardWrapper>

            {/* Inspiration Card */}
            <CardWrapper>
              <CardImage src={inspire} alt="Find Your Inspiration" />
              <CardContent>
                <CardTitle>
                  {t("inspirationTitle", "Find Your Inspiration in Fès-Meknès")}
                </CardTitle>
                <CardDescription>
                  {t(
                    "inspirationDescription",
                    "Explore curated experiences and themes that spark your curiosity, from cultural encounters to nature escapes, discover what makes every moment unforgettable."
                  )}
                </CardDescription>
                <CardButton onClick={handleGetInspired}>
                  {t("getInspired", "Get Inspired")}
                </CardButton>
              </CardContent>
            </CardWrapper>
          </CardsContainer>
        </ModalContainer>
      </ModalWrapper>
    </ModalOverlay>
  );
};

export default ModalPopup;
